import { gql } from "@apollo/client";

// ==================== PRODUCT QUERIES ====================

export const GET_PRODUCTS = gql`
  query GetProducts($sellerProfileId: ID) {
    products(sellerProfileId: $sellerProfileId) {
      id
      name
      description
      price
      memberPrice
      images
      categories
      isActive
      seller {
        id
        fName
        lName
      }
      sellerProfile {
        id
        businessName
        logoImage
        businessType
        isVerified
        isFeatured
      }
      enableForSyndicate
    }
  }
`;

export const GET_PRODUCT = gql`
  query GetProduct($id: ID!) {
    product(id: $id) {
      id
      name
      description
      price
      memberPrice
      type
      pricingModel
      stockLevel
      sku
      isActive
      images
      variants {
        variantId
        title
        price
        sku
        stockLevel
      }
      categories
      tags
      ingredients
      howToUse
      benefits
      isFeatured
      isBestSeller
      shipping {
        weight
        dimensions
        availableShippingMethods
        requiresSpecialHandling
      }
      shippingInfo
      availableFrom
      availableUntil
      seller {
        id
        fName
        lName
        email
      }
      sellerProfile {
        id
        businessName
        logoImage
        businessType
        isVerified
        isFeatured
      }
      enableForSyndicate
      lifeEssentialCategory
    }
  }
`;

// ==================== SELLER PROFILE QUERIES ====================

export const GET_SELLER_PROFILE = gql`
  query GetSellerProfile($id: ID!) {
    sellerProfile(id: $id) {
      id
      businessName
      description
      businessType
      logoImage
      isVerified
      isFeatured
      madeByClient {
        id
        fName
        lName
      }
    }
  }
`;

export const GET_ALL_SELLER_PROFILES = gql`
  query GetAllSellerProfiles {
    sellerProfiles {
      id
      businessName
      businessType
      logoImage
      isVerified
      isFeatured
    }
  }
`;

export const GET_SELLER_PROFILES_BY_CLIENT = gql`
  query GetSellerProfilesByClient($clientId: ID!) {
    sellerProfilesByClient(clientId: $clientId) {
      id
      businessName
      description
      businessType
      logoImage
      isVerified
      isFeatured
      contactEmail
      contactPhone
      offersDelivery
      offersPickup
      offersShipping
    }
  }
`;

export const GET_MY_SELLER_PROFILES = gql`
  query GetMySellerProfiles {
    mySellerProfiles {
      id
      businessName
      description
      businessType
      logoImage
      isVerified
      isFeatured
    }
  }
`;

// ==================== CART QUERIES ====================

export const GET_CART = gql`
  query GetCart($cartId: ID!) {
    cart(id: $cartId) {
      id
      items {
        id
        product {
          id
          name
          price
          variants {
            variantId
            title
            price
          }
        }
        quantity
        selectedVariantId
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
      expiresAt
    }
  }
`;

export const GET_CART_WITH_CLIENT = gql`
  query GetCartWithClient($cartId: ID!) {
    cart(id: $cartId) {
      id
      client {
        id
        email
      }
      items {
        id
        product {
          id
          name
          price
          variants {
            variantId
            title
            price
          }
        }
        quantity
        selectedVariantId
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
      expiresAt
    }
  }
`;

export const GET_ACTIVE_CART = gql`
  query GetActiveCart {
    activeCartByClient {
      id
      items {
        id
        product {
          id
          name
          price
        }
        quantity
        selectedVariantId
      }
    }
  }
`;

export const GET_CART_PREVIEW = gql`
  query GetCartPreview($cartId: ID!) {
    cart(id: $cartId) {
      id
      items {
        id
        product {
          id
          name
          price
          variants {
            variantId
            title
            price
          }
        }
        quantity
        selectedVariantId
      }
    }
  }
`;

// ==================== ORDER QUERIES ====================

export const GET_ORDER = gql`
  query GetOrder($orderId: ID!) {
    productOrder(id: $orderId) {
      id
      client {
        id
        email
        fName
        lName
        phoneNumber
        businessName
        businessRegistrationNumber
        isVerifiedSeller
        billingAddresses {
          name
          phone
          address {
            street
            city
            state
            postcode
            country
          }
          isDefault
          instructions
        }
        shippingAddresses {
          name
          phone
          address {
            street
            city
            state
            postcode
            country
          }
          isDefault
          instructions
        }

      }
      items {
        product {
          id
          name
          price
          images
        }
        quantity
        priceAtTime
        selectedVariantId
      }
      totalAmount
      status
      payment {
        status
        method
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_ORDER_PAYMENT_STATUS = gql`
  query GetOrderPaymentStatus($orderId: ID!) {
    productOrder(id: $orderId) {
      id
      payment {
        status
      }
      status
    }
  }
`;

export const GET_TENANT_CONFIG = gql`
  query GetTenantConfig($id: ID!) {
    tenant(id: $id) {
      id
      name
      apiKeys {
        stripePublicKey
      }
      branding {
        siteName
      }
    }
  }
`;

// ==================== PRODUCT MUTATIONS ====================

export const CREATE_PRODUCT = gql`
  mutation CreateProduct($input: ProductInput!) {
    createProduct(input: $input) {
      id
      name
      description
      price
      type
      pricingModel
      sku
      stockLevel
      lifeEssentialCategory
    }
  }
`;

export const UPDATE_PRODUCT = gql`
  mutation UpdateProduct($id: ID!, $input: ProductInput!) {
    updateProduct(id: $id, input: $input) {
      id
      name
      description
      price
      memberPrice
      type
      pricingModel
      stockLevel
      sku
      isActive
      images
      variants {
        variantId
        title
        price
        sku
        stockLevel
      }
      categories
      tags
      ingredients
      howToUse
      benefits
      isFeatured
      isBestSeller
      shipping {
        weight
        dimensions
        availableShippingMethods
        requiresSpecialHandling
      }
      shippingInfo
      availableFrom
      availableUntil
      sellerProfile {
        id
        businessName
        logoImage
        businessType
        isVerified
        isFeatured
      }
      enableForSyndicate
      lifeEssentialCategory
    }
  }
`;

export const DELETE_PRODUCT = gql`
  mutation DeleteProduct($id: ID!) {
    deleteProduct(id: $id)
  }
`;

// ==================== CART MUTATIONS ====================

export const ADD_TO_CART = gql`
  mutation AddToCart($input: CartItemInput!) {
    addToCart(input: $input) {
      id
      items {
        id
        product {
          id
          name
          price
        }
        quantity
        selectedVariantId
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
      expiresAt
    }
  }
`;

export const DELETE_CART = gql`
  mutation DeleteCart($cartId: ID!) {
    deleteCart(cartId: $cartId)
  }
`;

// ==================== ORDER MUTATIONS ====================

export const CREATE_PRODUCT_ORDER = gql`
  mutation CreateProductOrder($input: ProductOrderInput!) {
    createProductOrder(input: $input) {
      id
      client {
        id
        email
        fName
        lName
      }
      items {
        product {
          id
          name
          price
        }
        quantity
        priceAtTime
        selectedVariantId
      }
      totalAmount
      status
      payment {
        status
        method
      }
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_PAYMENT_INTENT = gql`
  mutation CreatePaymentIntentForProductOrder($orderId: String!) {
    createPaymentIntentForProductOrder(orderId: $orderId)
  }
`;

export const UPDATE_ORDER_PAYMENT = gql`
  mutation UpdateOrderPayment($orderId: ID!, $status: PaymentStatus!, $stripePaymentIntentId: String!) {
    updateOrderPayment(
      orderId: $orderId
      status: $status
      stripePaymentIntentId: $stripePaymentIntentId
    ) {
      id
      payment {
        status
        method
        stripePaymentIntentId
      }
      status
    }
  }
`;

// ==================== FILE UPLOAD MUTATIONS ====================

export const UPLOAD_UNENCRYPTED_FILE = gql`
  mutation UploadUnencrypted($file: Upload!) {
    uploadUnencryptedFile(file: $file)
  }
`;

// ==================== SHIPPING MUTATIONS ====================

export const ADD_SHIPPING_ADDRESS = gql`
  mutation AddShippingAddress($input: ClientShippingDetailsInput!) {
    addClientShippingAddress(input: $input) {
      id
      shippingAddresses {
        name
        phone
        address {
          street
          city
          state
          postcode
          country
        }
        isDefault
        instructions
      }
    }
  }
`;

// ==================== SUBSCRIPTIONS ====================

export const ORDER_SUBSCRIPTION = gql`
  subscription OnOrderUpdated($orderId: String!) {
    orderUpdated(orderId: $orderId) {
      id
      status
      payment {
        status
        method
        paidAt
        amountPaid
      }
    }
  }
`;
